import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 检查用户是否已登录
  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');

      if (!token || !savedUser) {
        setIsLoading(false);
        return;
      }

      // 验证token是否仍然有效
      try {
        const response = await apiService.get('/api/auth/me');
        if (response.success) {
          setUser(response.data);
        } else {
          // Token无效，清除本地存储
          logout();
        }
      } catch (error) {
        console.error('Token验证失败:', error);
        logout();
      }
    } catch (error) {
      console.error('检查认证状态失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 登录函数
  const login = (userData, accessToken, refreshToken) => {
    setUser(userData);
    apiService.setToken(accessToken);
    localStorage.setItem('authToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // 登出函数
  const logout = async () => {
    try {
      // 调用后端登出接口
      await apiService.logout();
    } catch (error) {
      console.error('登出请求失败:', error);
    } finally {
      // 无论后端登出是否成功，都清除本地状态
      setUser(null);
      apiService.clearToken();
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  };

  // 刷新token
  const refreshToken = async () => {
    try {
      const refreshTok = localStorage.getItem('refreshToken');
      if (!refreshTok) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.refreshToken(refreshTok);

      if (response.success) {
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        apiService.setToken(accessToken);
        localStorage.setItem('authToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        return true;
      }
    } catch (error) {
      console.error('Token刷新失败:', error);
      logout();
      return false;
    }
  };

  // 初始化时检查认证状态
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshToken,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;